import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState
} from "@tanstack/react-table";
import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";
import { Fragment, useState, type ReactNode } from "react";
import { EmptyState } from "./EmptyState";

type DataTableProps<T> = {
  columns: ColumnDef<T>[];
  data: T[];
  getRowId: (row: T) => string;
  selectedId?: string;
  onSelect?: (row: T) => void;
  renderExpandedRow?: (row: T) => ReactNode;
  emptyTitle?: string;
};

export function DataTable<T>({ columns, data, getRowId, selectedId, onSelect, renderExpandedRow, emptyTitle = "표시할 항목이 없습니다." }: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const table = useReactTable({
    data,
    columns,
    getRowId,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  });

  if (!data.length) {
    return <EmptyState title={emptyTitle} />;
  }

  return (
    <div className="table-frame">
      <table className="data-table">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const sorted = header.column.getIsSorted();
                return (
                  <th key={header.id} style={{ width: header.getSize() }}>
                    {header.isPlaceholder ? null : (
                      <button
                        type="button"
                        className="table-sort"
                        disabled={!header.column.getCanSort()}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {sorted === "asc" ? <ChevronUp size={13} /> : sorted === "desc" ? <ChevronDown size={13} /> : <ChevronsUpDown size={13} />}
                      </button>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const expanded = selectedId === row.id && renderExpandedRow;
            return (
              <Fragment key={row.id}>
                <tr
                  className={selectedId === row.id ? "is-selected" : ""}
                  onClick={() => onSelect?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
                {expanded ? (
                  <tr className="table-expanded-row">
                    <td colSpan={row.getVisibleCells().length}>
                      {renderExpandedRow(row.original)}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
